/**
 * Division Eligibility Calculator
 *
 * Client-side implementation mirroring DivisionEligibilityService
 * to determine wrestler eligibility for age divisions.
 */

const GRADE_MAP = {
  'pre-k': -1,
  prek: -1,
  'pre k': -1,
  k: 0,
  kindergarten: 0,
  1: 1,
  '1st': 1,
  '1st grade': 1,
  first: 1,
  2: 2,
  '2nd': 2,
  '2nd grade': 2,
  second: 2,
  3: 3,
  '3rd': 3,
  '3rd grade': 3,
  third: 3,
  4: 4,
  '4th': 4,
  '4th grade': 4,
  fourth: 4,
  5: 5,
  '5th': 5,
  '5th grade': 5,
  fifth: 5,
  6: 6,
  '6th': 6,
  '6th grade': 6,
  sixth: 6,
  7: 7,
  '7th': 7,
  '7th grade': 7,
  seventh: 7,
  8: 8,
  '8th': 8,
  '8th grade': 8,
  eighth: 8,
  9: 9,
  '9th': 9,
  '9th grade': 9,
  ninth: 9,
  freshman: 9,
  freshmen: 9,
  10: 10,
  '10th': 10,
  '10th grade': 10,
  tenth: 10,
  sophomore: 10,
  11: 11,
  '11th': 11,
  '11th grade': 11,
  eleventh: 11,
  junior: 11,
  12: 12,
  '12th': 12,
  '12th grade': 12,
  twelfth: 12,
  senior: 12,
  other: 99,
};

/**
 * Parse academic class string to grade number
 * @param {string} academicClass - e.g., "3rd grade", "Kindergarten"
 * @returns {number|null} - Grade number or null if not parseable
 */
export function parseGrade(academicClass) {
  if (!academicClass) {
    return null;
  }
  const key = academicClass.toString().toLowerCase().trim();
  return GRADE_MAP[key] !== undefined ? GRADE_MAP[key] : null;
}

/**
 * Check if a division is age-based (has min and max age)
 * @param {Object} division - Age division object
 * @returns {boolean}
 */
function isAgeBased(division) {
  return division.min_age != null && division.max_age != null;
}

/**
 * Check if a division is grade-based (has min and max grade)
 * @param {Object} division - Age division object
 * @returns {boolean}
 */
function isGradeBased(division) {
  return division.min_grade != null && division.max_grade != null;
}

/**
 * Get the birth year range for a division given a competition year
 * @param {Object} division - Age division object
 * @param {number} competitionYear - The year of the competition
 * @returns {Object} - { min: number, max: number }
 */
export function getBirthYearRange(division, competitionYear) {
  const minBirthYear = competitionYear - division.max_age;
  const maxBirthYear = competitionYear - division.min_age;
  return { min: minBirthYear, max: maxBirthYear };
}

/**
 * Check if a wrestler's birth year is eligible for a division
 * @param {Object} division - Age division object
 * @param {number} birthYear - Wrestler's birth year
 * @param {number} competitionYear - Competition year
 * @returns {boolean}
 */
function isAgeEligible(division, birthYear, competitionYear) {
  if (!isAgeBased(division)) {
    return true;
  }
  if (!birthYear) {
    return false;
  }
  const range = getBirthYearRange(division, competitionYear);
  return birthYear >= range.min && birthYear <= range.max;
}

/**
 * Check if a wrestler's grade is eligible for a division
 * @param {Object} division - Age division object
 * @param {number} wrestlerGrade - Wrestler's grade number
 * @returns {boolean}
 */
function isGradeEligible(division, wrestlerGrade) {
  if (!isGradeBased(division)) {
    return true;
  }
  if (wrestlerGrade == null) {
    return false;
  }
  return wrestlerGrade >= division.min_grade && wrestlerGrade <= division.max_grade;
}

/**
 * Check if wrestler's gender matches division requirements
 * @param {Object} division - Age division object (gender: 'male', 'female', or 'any')
 * @param {string} wrestlerGender - Wrestler's gender ('male' or 'female')
 * @returns {boolean}
 */
function genderMatches(division, wrestlerGender) {
  if (division.gender === 'any') {
    return true;
  }
  if (!wrestlerGender) {
    return false;
  }
  return division.gender === wrestlerGender;
}

/**
 * Check if a wrestler is eligible for a specific division
 * @param {Object} wrestler - Wrestler profile object with dob, academic_class, gender
 * @param {Object} division - Age division object
 * @param {number} competitionYear - Competition year
 * @returns {boolean}
 */
export function isEligibleForDivision(wrestler, division, competitionYear) {
  // Extract birth year from dob if present
  let birthYear = null;
  if (wrestler.dob) {
    const dobDate = new Date(wrestler.dob);
    birthYear = dobDate.getFullYear();
  }

  // Parse grade
  const wrestlerGrade = parseGrade(wrestler.academic_class);

  // Check all three criteria
  return (
    isAgeEligible(division, birthYear, competitionYear) &&
    isGradeEligible(division, wrestlerGrade) &&
    genderMatches(division, wrestler.gender)
  );
}

/**
 * Get a human-readable ineligibility reason for a wrestler and division
 * @param {Object} wrestler - Wrestler profile object
 * @param {Object} division - Age division object
 * @param {number} competitionYear - Competition year
 * @returns {string|null} - Reason string or null if eligible
 */
export function getIneligibilityReason(wrestler, division, competitionYear) {
  const reasons = [];

  // Check gender
  if (!genderMatches(division, wrestler.gender)) {
    if (!wrestler.gender) {
      reasons.push('gender is required on wrestler profile');
    } else {
      reasons.push(`this division is ${division.gender === 'male' ? 'boys' : 'girls'} only`);
    }
  }

  // Check age
  if (isAgeBased(division)) {
    let birthYear = null;
    if (wrestler.dob) {
      const dobDate = new Date(wrestler.dob);
      birthYear = dobDate.getFullYear();
    }

    if (!birthYear) {
      reasons.push('date of birth is required on wrestler profile');
    } else if (!isAgeEligible(division, birthYear, competitionYear)) {
      const range = getBirthYearRange(division, competitionYear);
      reasons.push(`born in ${birthYear}, requires ${range.min}-${range.max}`);
    }
  }

  // Check grade
  if (isGradeBased(division)) {
    const wrestlerGrade = parseGrade(wrestler.academic_class);

    if (wrestlerGrade == null) {
      reasons.push('grade is required on wrestler profile');
    } else if (!isGradeEligible(division, wrestlerGrade)) {
      const gradeRange = division.grade_display
        ? division.grade_display
        : `${division.min_grade}-${division.max_grade}`;
      reasons.push(`wrestler is in ${wrestler.academic_class}, requires ${gradeRange}`);
    }
  }

  if (reasons.length === 0) {
    return null;
  }

  return `Ineligible: ${reasons.join(', ')}`;
}

/**
 * Get a summary of which requirement categories a wrestler fails for a division
 * e.g., "age or gender requirements", "grade requirements"
 * @param {Object} wrestler - Wrestler profile object
 * @param {Object} division - Age division object
 * @param {number} competitionYear - Competition year
 * @returns {string|null} - Category summary or null if eligible
 */
export function getIneligibilityCategory(wrestler, division, competitionYear) {
  const failedCategories = [];

  if (!genderMatches(division, wrestler.gender)) {
    failedCategories.push('gender');
  }

  if (isAgeBased(division)) {
    let birthYear = null;
    if (wrestler.dob) {
      birthYear = new Date(wrestler.dob).getFullYear();
    }
    if (!birthYear || !isAgeEligible(division, birthYear, competitionYear)) {
      failedCategories.push('age');
    }
  }

  if (isGradeBased(division)) {
    const wrestlerGrade = parseGrade(wrestler.academic_class);
    if (wrestlerGrade == null || !isGradeEligible(division, wrestlerGrade)) {
      failedCategories.push('grade');
    }
  }

  if (failedCategories.length === 0) {
    return null;
  }

  return `${failedCategories.join(' or ')} requirements`;
}

/**
 * Filter divisions to only those enabled for a session
 * @param {Object} session - Paid session with division_standard and enabled_age_division_ids
 * @returns {Array} - Enabled age divisions
 */
export function getEnabledDivisions(session) {
  if (!session.division_standard || !session.enabled_age_division_ids) {
    return [];
  }

  const enabledIds = session.enabled_age_division_ids;
  return session.division_standard.age_divisions.filter((div) => enabledIds.includes(div.id));
}

/**
 * Check if a wrestler has all required fields for division eligibility checks
 * @param {Object} wrestler - Wrestler profile object
 * @returns {Object} - { valid: boolean, missingFields: string[] }
 */
export function validateWrestlerForDivisions(wrestler) {
  const missingFields = [];

  if (!wrestler.dob) {
    missingFields.push('date of birth');
  }
  if (!wrestler.academic_class) {
    missingFields.push('grade');
  }
  if (!wrestler.gender) {
    missingFields.push('gender');
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Find the weight class closest to a given weight
 * @param {Array} weightClasses - Array of weight class objects
 * @param {number|string} wrestlerWeight - Wrestler's weight
 * @returns {Object|null} - Closest weight class or null
 */
export function findClosestWeightClass(weightClasses, wrestlerWeight) {
  if (!weightClasses || weightClasses.length === 0 || !wrestlerWeight) {
    return null;
  }

  const weight = parseFloat(wrestlerWeight);
  if (isNaN(weight)) {
    return null;
  }

  let closest = null;
  let closestDiff = Infinity;

  weightClasses.forEach((wc) => {
    const diff = Math.abs(wc.weight_value - weight);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = wc;
    }
  });

  return closest;
}
